import { useEffect, useState } from 'react';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

let toastId = 0;

export function showToast(message: string, type: ToastMessage['type'] = 'info') {
  const event = new CustomEvent('app-toast', {
    detail: { id: ++toastId, message, type },
  });
  window.dispatchEvent(event);
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const handler = ((e: CustomEvent<ToastMessage>) => {
      const toast = e.detail;
      setToasts((prev) => [...prev, toast]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, 4000);
    }) as (e: Event) => void;
    window.addEventListener('app-toast', handler);
    return () => window.removeEventListener('app-toast', handler);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => {
        const colors = {
          success: 'bg-green-900/90 border-green-500/30 text-green-100',
          error: 'bg-red-900/90 border-red-500/30 text-red-100',
          warning: 'bg-yellow-900/90 border-yellow-500/30 text-yellow-100',
          info: 'bg-brand-card/95 border-brand-gold/30 text-brand-text',
        };
        const icons = {
          success: 'ri-check-line text-green-400',
          error: 'ri-close-circle-line text-red-400',
          warning: 'ri-alert-line text-yellow-400',
          info: 'ri-information-line text-brand-gold',
        };
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-2 px-4 py-3 rounded-lg border text-sm shadow-lg backdrop-blur-sm min-w-[280px] max-w-md animate-[fadeIn_0.2s_ease-out] ${colors[toast.type]}`}
          >
            <i className={`${icons[toast.type]} text-lg`} />
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-white/10 transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        );
      })}
    </div>
  );
}